import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getQuestionnaire } from '../services/questionnaireService';
import { submitResponse } from '../services/responseService';
import { useAuth } from '../context/AuthContext';
import FieldRenderer from '../components/consultation/FieldRenderer';
import MainLayout from '../layouts/MainLayout';

const QuestionnairePage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    getQuestionnaire(id)
      .then((res) => {
        setQuestionnaire(res.data);
        // Initialize answers with empty values
        const initial = {};
        res.data.fields.forEach((f) => { initial[f.fieldKey] = ''; });
        setAnswers(initial);
      })
      .catch(() => setError('Questionnaire not found.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleChange = (fieldKey, value) => {
    setAnswers((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    const formattedAnswers = Object.entries(answers).map(([fieldKey, value]) => ({ fieldKey, value }));

    try {
      await submitResponse({
        tenantId: questionnaire.tenantId,
        projectId: questionnaire.projectId,
        questionnaireId: questionnaire._id,
        answers: formattedAnswers,
        responseScope: user ? 'community_only' : 'public',
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <MainLayout><p>Loading...</p></MainLayout>;
  if (error && !questionnaire) return <MainLayout><p className="error-msg">{error}</p></MainLayout>;

  if (submitted) {
    return (
      <MainLayout>
        <div className="page questionnaire-page">
          <h2>Thank You</h2>
          <p>Your response has been submitted.</p>
          <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="page questionnaire-page">
        <h2>{questionnaire.title}</h2>
        {questionnaire.description && <p className="q-description">{questionnaire.description}</p>}

        {error && <div className="error-msg">{error}</div>}

        <form onSubmit={handleSubmit}>
          {[...questionnaire.fields].sort((a, b) => a.order - b.order).map((field) => (
            <div key={field.fieldKey} className="form-field">
              <label>
                {field.label}
                {field.isRequired && <span className="required"> *</span>}
              </label>
              <FieldRenderer
                field={field}
                value={answers[field.fieldKey] ?? ''}
                onChange={handleChange}
              />
            </div>
          ))}
          <button type="submit" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Response'}
          </button>
        </form>
      </div>
    </MainLayout>
  );
};

export default QuestionnairePage;
